import {
  type DocumentPickerAsset,
  type DocumentPickerOptions,
  getDocumentAsync,
} from "expo-document-picker";
import { useCallback, useState } from "react";

export type UseFilePickerOptions = {
  /**
   * Fires once the user finishes selecting files. Return a `Promise` to keep
   * `isLoading` true until your upload work settles; the hook awaits it inside
   * its own `try/finally` and clears loading when the promise resolves.
   */
  onFilesSelected: (files: DocumentPickerAsset[]) => Promise<void> | void;
  /** Fires when the user dismisses the picker without selecting files. */
  onCanceled?: () => void;
  /** MIME types to allow. Defaults to PDF + JPEG/PNG. */
  accept?: DocumentPickerOptions["type"];
  /** Allow selecting more than one file. Defaults to true. */
  multiple?: boolean;
};

export type UseFilePickerResult = {
  /** Opens the OS document picker; resolves once the picker is dismissed. */
  pickFile: () => Promise<void>;
  /** True from `pickFile` invocation until `onFilesSelected` settles. */
  isLoading: boolean;
};

const defaultAccept: DocumentPickerOptions["type"] = [
  "application/pdf",
  "image/jpeg",
  "image/png",
];

/**
 * Wraps `expo-document-picker`'s `getDocumentAsync`. Owns its own `isLoading`
 * state and awaits the consumer's `onFilesSelected` callback so the loading
 * affordance stays up while uploads complete.
 */
export const useFilePicker = ({
  onFilesSelected,
  onCanceled,
  accept = defaultAccept,
  multiple = true,
}: UseFilePickerOptions): UseFilePickerResult => {
  const [isLoading, setIsLoading] = useState(false);

  const pickFile = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getDocumentAsync({
        type: accept,
        multiple,
      });
      if (result.canceled) {
        onCanceled?.();
        return;
      }
      await onFilesSelected(result.assets);
    } catch (error) {
      console.error("Error picking document", error);
    } finally {
      setIsLoading(false);
    }
  }, [accept, multiple, onCanceled, onFilesSelected]);

  return { pickFile, isLoading };
};
